'use client';

import React from 'react';

interface NewsPaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  className?: string;
}

export default function NewsPagination({
  currentPage,
  totalPages,
  totalCount,
  itemsPerPage,
  onPageChange,
  className = ''
}: NewsPaginationProps) {
  if (totalPages <= 1) {
    return null;
  }

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 ${className}`}>
      {/* Информация о количестве */}
      <div className="text-sm text-site-muted">
        Показано {startItem}-{endItem} из {totalCount} новостей
      </div>

      {/* Пагинация */}
      <div className="flex items-center gap-2">
        {/* Предыдущая страница */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-3 py-2 rounded-lg text-sm transition-colors ${
            currentPage === 1
              ? 'bg-site-raised/30 text-site-muted cursor-not-allowed'
              : 'bg-site-raised/50 text-site-secondary hover:bg-site-raised/50 hover:text-site-text'
          }`}
        >
          ← Предыдущая
        </button>

        {/* Номера страниц */}
        <div className="flex items-center gap-1">
          {getVisiblePages().map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <span className="px-3 py-2 text-site-muted">...</span>
              ) : (
                <button
                  onClick={() => onPageChange(page as number)}
                  className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                    currentPage === page
                      ? 'bg-site-accent text-site-on-accent'
                      : 'bg-site-raised/50 text-site-secondary hover:bg-site-raised/50 hover:text-site-text'
                  }`}
                >
                  {page}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Следующая страница */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`px-3 py-2 rounded-lg text-sm transition-colors ${
            currentPage === totalPages
              ? 'bg-site-raised/30 text-site-muted cursor-not-allowed'
              : 'bg-site-raised/50 text-site-secondary hover:bg-site-raised/50 hover:text-site-text'
          }`}
        >
          Следующая →
        </button>
      </div>
    </div>
  );
}
